import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  getFilingDetails,
  updateNotarisationOfDocuments,
  type FilingDetailsResponse,
} from "../../api/discussion";

type NotarisationRow = {
  classNumber: number;
  typeOfMark: string;
  stampPapersUa: number;
  amountPayableToNotary: number;
  paidToNotary: boolean | null;
  paymentStatusDisplay: string;
  userDate?: string | null;
};

const CELL_READONLY =
  "bg-[#f7f7f7] border border-[#eee] flex flex-col md:flex-row min-h-[44px] md:h-10 md:min-h-0 md:items-center justify-center md:justify-between px-[10px] py-2 rounded-[6px] shrink-0 gap-0.5 md:gap-0";
const PLACEHOLDER_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";

function buildUserDateMap(
  userDatesPerMark: FilingDetailsResponse["filingDetails"]["userDatesPerMark"]
): Map<string, string> {
  const map = new Map<string, string>();
  if (!userDatesPerMark?.length) return map;
  for (const ud of userDatesPerMark) {
    const key = `${ud.classNumber}::${ud.typeOfMark}`;
    if (ud.date) map.set(key, ud.date);
  }
  return map;
}

type NotarisationOfDocumentsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

/**
 * Notarisation of Documents - Filing step view
 * Table: Classes | Type Mark | User Date | Status/Amount
 */
export default function NotarisationOfDocumentsView({
  leadId,
  onSuccess,
}: NotarisationOfDocumentsViewProps) {
  const [rows, setRows] = useState<NotarisationRow[]>([]);
  const [userDateMap, setUserDateMap] = useState<Map<string, string>>(new Map());
  const [isStepActive, setIsStepActive] = useState(false);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFilingDetails(leadId)
      .then((res) => {
        if (cancelled) return;
        setIsStepActive(res.isNotarisationStepActive ?? false);
        const fd = res.filingDetails;
        const perApp = fd.notarisationPerApplication ?? [];
        setRows(
          perApp.map((r) => ({
            classNumber: r.classNumber,
            typeOfMark: r.typeOfMark,
            stampPapersUa: r.stampPapersUa,
            amountPayableToNotary: r.amountPayableToNotary,
            paidToNotary: r.paidToNotary,
            paymentStatusDisplay: r.paymentStatusDisplay,
          }))
        );
        setUserDateMap(buildUserDateMap(fd.userDatesPerMark));
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load filing details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const updateRowPaidToNotary = (idx: number, paidToNotary: boolean) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx ? { ...r, paidToNotary } : r
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const payableRows = rows.filter((r) => r.stampPapersUa > 0);
    if (payableRows.length === 0) {
      toast.error(
        "No applications have UA stamp papers yet. Complete the Stamp Paper step first.",
      );
      return;
    }
    setSaving(true);
    try {
      const res = await updateNotarisationOfDocuments(leadId, {
        notarisationPerApplication: payableRows.map((r) => ({
          classNumber: r.classNumber,
          typeOfMark: r.typeOfMark,
          amountPayableToNotary: r.amountPayableToNotary,
          paidToNotary: r.paidToNotary ?? undefined,
        })),
      });
      if (res.notarisationPerApplication?.length) {
        setRows(
          res.notarisationPerApplication.map((r) => ({
            classNumber: r.classNumber,
            typeOfMark: r.typeOfMark,
            stampPapersUa: r.stampPapersUa,
            amountPayableToNotary: r.amountPayableToNotary,
            paidToNotary: r.paidToNotary,
            paymentStatusDisplay: r.paymentStatusDisplay,
          }))
        );
      }
      toast.success("Notarisation details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const formatUserDate = (dateStr: string | undefined): string => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading filing details...</p>
      </div>
    );
  }

  if (!isStepActive) {
    return (
      <div
        className="flex flex-col items-center w-full"
        style={{
          fontFamily: "Inter, sans-serif",
          gap: 22,
          fontFeatureSettings: "'ss01' on, 'cv01' on",
        }}
      >
        <div
          className="flex flex-col items-center w-full max-w-[1085px]"
          style={{ gap: 20 }}
        >
          <div
            className="flex items-center text-[#222222]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "20px",
              fontFeatureSettings: "'ss01' on, 'cv01' on",
            }}
          >
            Not Required
          </div>
          <div className="flex flex-row justify-center items-center gap-4 md:gap-5 px-4 md:px-0">
            <div className="flex flex-col justify-center items-center gap-4 md:gap-5 max-w-[271px]">
              <div
                className="flex items-center text-center text-[#222222] text-[12px] md:text-xs leading-5"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontFeatureSettings: "'ss01' on, 'cv01' on",
                }}
              >
                The User Date is set to &quot;Proposed to be used&quot;.
                Notarisation is not required as User Affidavit is skipped.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      id="notarisation-of-documents-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Table header - hidden on mobile */}
      <div
        className="hidden md:flex font-medium gap-5 items-center text-[#222] text-sm leading-5"
        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        <div className="shrink-0 w-[160px]">Classes</div>
        <div className="shrink-0 w-[230px]">Type Mark</div>
        <div className="shrink-0 w-[170px]">User Date</div>
        <div className="shrink-0 w-[160px]">Status / Amount</div>
      </div>

      {/* Table rows - card on mobile, row on desktop */}
      <div className="flex flex-col gap-3 w-full">
        {rows.map((row, idx) => {
          const udKey = `${row.classNumber}::${row.typeOfMark}`;
          const userDateStr = userDateMap.get(udKey);
          const statusValue = row.paidToNotary === true ? "paid" : "pending";
          return (
            <div
              key={`${row.classNumber}-${row.typeOfMark}`}
              className="flex flex-col md:flex-row gap-2 md:gap-5 md:h-10 items-stretch md:items-center w-full p-3 md:p-0 rounded-[6px] border border-[#EEE] bg-white md:bg-transparent md:border-0"
            >
              {/* Classes - read-only gray */}
              <div className={`${CELL_READONLY} w-full md:w-[160px]`}>
                <span className="md:hidden text-[12px] font-normal text-[#6d6d6d]">Classes</span>
                <span
                  className="text-[12px] md:text-sm font-normal text-[#222] whitespace-nowrap"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  Class {row.classNumber}
                </span>
              </div>

              {/* Type Mark - read-only */}
              <div className={`${CELL_READONLY} w-full md:w-[230px]`}>
                <span className="md:hidden text-[12px] font-normal text-[#6d6d6d]">Type Mark</span>
                <span
                  className="text-[12px] md:text-sm font-normal text-[#222] truncate min-w-0"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                  title={row.typeOfMark}
                >
                  {row.typeOfMark || "—"}
                </span>
              </div>

              {/* User Date - read-only from filing details */}
              <div className={`${CELL_READONLY} w-full md:w-[170px]`}>
                <span className="md:hidden text-[12px] font-normal text-[#6d6d6d]">User Date</span>
                <span
                  className={`text-[12px] md:text-xs ${
                    userDateStr ? "text-[#222]" : PLACEHOLDER_CLASS
                  }`}
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {userDateStr ? formatUserDate(userDateStr) : "Select Date"}
                </span>
              </div>

              {/* Status / Amount - editable dropdown, or read-only "Not applicable" when no UA stamp papers */}
              <div className="w-full md:w-[160px] shrink-0 flex flex-col gap-1 md:gap-0">
                <span className="md:hidden text-[12px] font-normal text-[#6d6d6d]">Status / Amount</span>
                {row.stampPapersUa > 0 ? (
                  <Select
                    value={statusValue}
                    onValueChange={(v) =>
                      updateRowPaidToNotary(idx, v === "paid")
                    }
                  >
                    <SelectTrigger className="min-h-[44px] md:h-10 md:min-h-[40px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs data-[placeholder]:text-[#6D6D6D] touch-manipulation">
                      <SelectValue>
                        {row.paidToNotary === true ? "Paid" : "Pending Payment"} (Rs.{" "}
                        {row.amountPayableToNotary})
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">
                        Paid (Rs. {row.amountPayableToNotary})
                      </SelectItem>
                      <SelectItem value="pending">
                        Pending Payment (Rs. {row.amountPayableToNotary})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div
                    className={`${CELL_READONLY} w-full !min-h-[44px] md:!h-10 md:!min-h-[40px]`}
                    title="Stamp papers not entered for this mark — complete the Stamp Paper step first."
                  >
                    <span className={`${PLACEHOLDER_CLASS} truncate`}>
                      Not applicable — no UA stamp papers
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rows.length === 0 && (
        <p className="text-[12px] md:text-sm text-[#6D6D6D] py-4">
          No notarisation rows. Complete the earlier steps (Stamp Paper details, User
          Affidavit) so that applications with UA stamp papers appear here.
        </p>
      )}

    </form>
  );
}
